import { FC, useState } from 'react';
import { Row, Col } from 'antd';
import { TRtkErrorType } from 'data/types/generic.types';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import useForm from 'hooks/useForm';
import { useAppDispatch } from 'redux/store';
import { registrantsApi } from 'redux/apiSlice/registrants.slice';
import { useAddEventRegistrationMutation } from 'redux/apiSlice/events.slice';
import useGetValidationData from 'components/Base/Form/validationRules';
import {
  formatPhoneWhileTyping,
  optionalPhoneValidator,
} from 'helpers/phoneUtils';
import { isFormValidationError } from 'helpers/formUtils';
import { Modal } from 'components/Base/Modal';
import { Form, FormItem } from 'components/Base/Form';
import { Input, TextArea } from 'components/Base/Input';
import {
  TAddEventRegistrationForm,
  TAddEventRegistrationModalProps,
} from './AddRegistrationModal.types';

const AddRegistrationModal: FC<TAddEventRegistrationModalProps> = ({
  open,
  setOpen,
  eventId,
  event,
}) => {
  const customQuestion = event.customQuestion?.value;
  const dispatch = useAppDispatch();
  const { showToastNotification } = useShowToastNotification();
  const { validationRules } = useGetValidationData();
  const { form } = useForm<TAddEventRegistrationForm>();
  const [addEventRegistration, { isLoading }] =
    useAddEventRegistrationMutation();
  const [phoneValue, setPhoneValue] = useState('');

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneWhileTyping(e.target.value);
    setPhoneValue(formatted);
    form.setFieldValue('phone', formatted);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const { firstName, lastName, email, phone, customQuestionAnswer } =
        values;

      await addEventRegistration({
        eventId,
        firstName,
        lastName,
        email,
        phone,
        customQuestionAnswer: customQuestion
          ? customQuestionAnswer?.trim() || undefined
          : undefined,
      }).unwrap();

      // Refresh the Registrants, Attendance, and Waitlist tables on the
      // details page. The add-registration mutation lives in eventsApi
      // (which cannot import registrantsApi without a circular import), so
      // we invalidate the shared tags from here where both APIs are already
      // in scope. `event-registrants` feeds both the Registrants and
      // Attendance tabs; `wait-list` feeds the Waitlist tab (a new
      // registration can affect wait-list ordering / promotion).
      dispatch(
        registrantsApi.util.invalidateTags([
          { type: 'event-registrants', id: eventId },
          { type: 'wait-list', id: eventId },
        ]),
      );

      showToastNotification({
        message: 'Registration added successfully!',
        type: ENotificationType.SUCCESS,
      });

      setOpen(false);
    } catch (e) {
      if (isFormValidationError(e)) {
        return;
      }

      showToastNotification({
        message:
          (e as TRtkErrorType)?.data?.message || 'Failed to add registration',
        type: ENotificationType.ERROR,
      });
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setPhoneValue('');
    setOpen(false);
  };

  return (
    <Modal
      title="Add Registration"
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText="Add"
      cancelText="Cancel"
      confirmLoading={isLoading}
      width={600}
      destroyOnClose
      centered
    >
      <Form form={form} layout="vertical" requiredMark>
        <Row gutter={24}>
          <Col span={12}>
            <FormItem
              name="firstName"
              label="First Name"
              rules={[validationRules.required('First Name')]}
              required
            >
              <Input />
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem
              name="lastName"
              label="Last Name"
              rules={[validationRules.required('Last Name')]}
              required
            >
              <Input />
            </FormItem>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <FormItem
              name="email"
              label="Email"
              rules={[validationRules.email()]}
            >
              <Input />
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem
              name="phone"
              label="Phone"
              rules={[{ validator: optionalPhoneValidator }]}
            >
              <Input
                value={phoneValue}
                onChange={handlePhoneChange}
                placeholder="(555) 555-5555"
              />
            </FormItem>
          </Col>
        </Row>
        {customQuestion && (
          <FormItem name="customQuestionAnswer" label={customQuestion}>
            <TextArea maxLength={144} showCount />
          </FormItem>
        )}
      </Form>
    </Modal>
  );
};

export default AddRegistrationModal;
